// Persona Utility Functions
// Maps persona IDs to their display information

import { DatingPersona, PersonaQuestion } from '../components/registration/types';

export interface PersonaInfo {
  id: string;
  name: string;
  tagline: string;
  icon?: string;
}

export const PERSONA_MAP: Record<string, PersonaInfo> = {
  flow_seeker: {
    id: 'flow_seeker',
    name: 'The Flow Seeker',
    tagline: 'Spontaneous, fun-loving, brings joy to every moment',
    icon: '🌊'
  },
  purpose_builder: {
    id: 'purpose_builder',
    name: 'The Purpose Builder',
    tagline: 'Ambitious, reliable, builds meaningful connections',
    icon: '🎯'
  },
  deep_connector: {
    id: 'deep_connector',
    name: 'The Deep Connector',
    tagline: 'Thoughtful, authentic, seeks meaningful relationships',
    icon: '🔍'
  },
  dynamic_explorer: {
    id: 'dynamic_explorer',
    name: 'The Dynamic Explorer',
    tagline: 'Adventurous, energetic, always up for new experiences',
    icon: '⚡'
  },
  steady_guardian: {
    id: 'steady_guardian',
    name: 'The Steady Guardian',
    tagline: 'Loyal, caring, creates safe spaces for love to grow',
    icon: '🛡️'
  },
  visionary_creator: {
    id: 'visionary_creator',
    name: 'The Visionary Creator',
    tagline: 'Independent, innovative, thinks outside the box',
    icon: '🌟'
  }
};

/**
 * Get persona information by ID
 */
export const getPersonaInfo = (personaId?: string | null): PersonaInfo | null => {
  if (!personaId) return null;
  return PERSONA_MAP[personaId] || null;
};

/**
 * Get persona display name
 */
export const getPersonaName = (personaId?: string | null): string | null => {
  const persona = getPersonaInfo(personaId);
  return persona?.name || null;
};

export const AVAILABLE_PERSONAS: DatingPersona[] = [
    { id: 'flow_seeker', name: 'The Flow Seeker', tagline: 'Spontaneous, fun-loving, brings joy to every moment', traits: ['spontaneous', 'social', 'optimistic', 'creative'], myersBriggsTypes: ['ENFP', 'ESFP'], visualIdentity: { colors: ['#FF6B6B', '#4ECDC4', '#45B7D1'], shapes: ['flowing', 'dynamic', 'organic'], imagery: ['🌊', '🎨', '🎭'] } },
    { id: 'purpose_builder', name: 'The Purpose Builder', tagline: 'Ambitious, reliable, builds meaningful connections', traits: ['goal-oriented', 'reliable', 'leadership', 'strategic'], myersBriggsTypes: ['ENTJ', 'ESTJ'], visualIdentity: { colors: ['#2C3E50', '#34495E', '#7F8C8D'], shapes: ['geometric', 'structured', 'angular'], imagery: ['🎯', '🏗️', '📊'] } },
    { id: 'deep_connector', name: 'The Deep Connector', tagline: 'Thoughtful, authentic, seeks meaningful relationships', traits: ['introspective', 'creative', 'authentic', 'empathetic'], myersBriggsTypes: ['INFP', 'ISFP'], visualIdentity: { colors: ['#8E44AD', '#9B59B6', '#E74C3C'], shapes: ['organic', 'flowing', 'soft'], imagery: ['🔍', '💎', '🌸'] } },
    { id: 'dynamic_explorer', name: 'The Dynamic Explorer', tagline: 'Adventurous, energetic, always up for new experiences', traits: ['adventurous', 'energetic', 'spontaneous', 'innovative'], myersBriggsTypes: ['ENTP', 'ESTP'], visualIdentity: { colors: ['#E67E22', '#F39C12', '#F1C40F'], shapes: ['dynamic', 'angular', 'bold'], imagery: ['⚡', '🚀', '🌟'] } },
    { id: 'steady_guardian', name: 'The Steady Guardian', tagline: 'Loyal, caring, creates safe spaces for love to grow', traits: ['loyal', 'caring', 'stable', 'protective'], myersBriggsTypes: ['ISFJ', 'ISTJ'], visualIdentity: { colors: ['#27AE60', '#2ECC71', '#16A085'], shapes: ['rounded', 'stable', 'protective'], imagery: ['🛡️', '🌱', '🏠'] } },
    { id: 'visionary_creator', name: 'The Visionary Creator', tagline: 'Independent, innovative, thinks outside the box', traits: ['independent', 'innovative', 'analytical', 'future-focused'], myersBriggsTypes: ['INTJ', 'INTP'], visualIdentity: { colors: ['#3498DB', '#2980B9', '#1ABC9C'], shapes: ['geometric', 'modern', 'clean'], imagery: ['🌟', '💡', '🔬'] } }
];

export const PERSONA_QUESTIONS: Record<string, PersonaQuestion[]> = {
    flow_seeker: [{ id: 'q_flow_1', persona: 'flow_seeker', question: 'Your ideal first date would be...', options: [{ value: 'A', label: 'Spontaneous road trip', icon: '🚗' }, { value: 'B', label: 'Planned dinner', icon: '🍽️' }, { value: 'C', label: 'Casual coffee', icon: '☕' }, { value: 'D', label: 'Adventure activity', icon: '🏔️' }], correctAnswer: 'A' }],
    purpose_builder: [{ id: 'q_purpose_1', persona: 'purpose_builder', question: 'When planning your week, you prefer to...', options: [{ value: 'A', label: 'Have a detailed schedule', icon: '📅' }, { value: 'B', label: 'Keep it flexible', icon: '🔄' }, { value: 'C', label: 'One main priority', icon: '🎯' }], correctAnswer: 'A' }],
    deep_connector: [{ id: 'q_deep_1', persona: 'deep_connector', question: 'In a relationship, you value most...', options: [{ value: 'A', label: 'Deep emotional connection', icon: '💝' }, { value: 'B', label: 'Shared adventures', icon: '🗺️' }, { value: 'C', label: 'Intellectual convos', icon: '💭' }], correctAnswer: 'A' }],
    dynamic_explorer: [{ id: 'q_dynamic_1', persona: 'dynamic_explorer', question: 'What gets your heart racing?', options: [{ value: 'A', label: 'Extreme sports', icon: '🏄‍♂️' }, { value: 'B', label: 'Meeting new people', icon: '🎉' }, { value: 'C', label: 'New business venture', icon: '🚀' }], correctAnswer: 'A' }],
    steady_guardian: [{ id: 'q_guardian_1', persona: 'steady_guardian', question: 'What makes you feel most fulfilled?', options: [{ value: 'A', label: 'Helping someone', icon: '🤝' }, { value: 'B', label: 'Comfortable home', icon: '🏠' }, { value: 'C', label: 'Being reliable', icon: '🛡️' }], correctAnswer: 'A' }],
    visionary_creator: [{ id: 'q_vision_1', persona: 'visionary_creator', question: 'What excites you most about the future?', options: [{ value: 'A', label: 'Tech innovations', icon: '🤖' }, { value: 'B', label: 'Solving problems', icon: '🧩' }, { value: 'C', label: 'Creating something', icon: '💡' }], correctAnswer: 'A' }]
};
